import React, {useEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {useUserGradeSelector, useUserSelector} from "../../../../../store/selectors/authorization.selectors";
import {faAngleDown, faAngleUp, faLock} from "@fortawesome/pro-light-svg-icons";
import {
    faSquarePlus,
    faTrashCheck,
    faArrowDownFromDottedLine,
    faCalendarCircleExclamation,
    faCalendarCirclePlus,
    faCalendarCircleMinus,
    faCircleCheck,
    faCirclePlay,
    faTextSlash,
    faFolderTree
} from "@fortawesome/pro-regular-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faAngleLeft, faAngleRight} from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import styles from "../ChangeAnalysisPage.module.scss"
import moment from "moment";


interface InjectedProps {
    title: string;
    headline: string;
    text: string;
    changeList: any[];
    total: number;
}

const generateText = (headline: string, task: any) => {
    switch (headline) {
        case "New Tasks":
            return task.actEndDate ? `Complete ${moment(task.actEndDate.toDate()).format("MMM DD, YYYY")}` :
                task.actStartDate ? `Started on ${moment(task.actStartDate.toDate()).format("MMM DD, YYYY")} - Priority ${task.index}` :
                `Priority ${task.index}`;
        case "Deleted Tasks":
            return task.actEndDate ? `Complete ${moment(task.actEndDate.toDate()).format("MMM DD, YYYY")}` :
                task.actStartDate ? `Started on ${moment(task.actStartDate.toDate()).format("MMM DD, YYYY")} - Priority ${task.index}` :
                    `Priority ${task.index}`;
        case "Tasks Completed":
            return `Completed on ${moment(task.actEndDate.toDate()).format("MMM DD, YYYY")}`;
        case "Tasks Started":
            return `Started on ${moment(task.actStartDate.toDate()).format("MMM DD, YYYY")}`;
        case "Task Duration Changes":
            return `Duration changed from ${task.targetDuration/2} hours to ${task.newDuration/2} hours`;
        case "Task Name Changes":
            return `Name changed to "${task.newTaskName}"`;
        case "Task WBS Changes":
            return `WBS changed to "${task.newWBS}"`;
        case "Tasks Delayed":
            return `New Early End Date: ${moment(task.earlyEndNew.toDate()).format("MMM DD, YYYY")}`;
        case "Tasks Ahead":
            return `New Early End Date: ${moment(task.earlyEndNew.toDate()).format("MMM DD, YYYY")}`;
        case "Logic Changes":
            return `Logic changed.`;
        default:
            return null;
    }
}

const selectIcon = (headline: string) => {
    switch (headline) {
        case "New Tasks":
            return faSquarePlus;
        case "Deleted Tasks":
            return faTrashCheck;
        case "Logic Changes":
            return faArrowDownFromDottedLine;
        case "Task Duration Changes":
            return faCalendarCircleExclamation;
        case "Tasks Delayed":
            return faCalendarCirclePlus;
        case "Tasks Ahead":
            return faCalendarCircleMinus;
        case "Tasks Completed":
            return faCircleCheck;
        case "Tasks Started":
            return faCirclePlay;
        case "Task Name Changes":
            return faTextSlash;
        case "Task WBS Changes":
            return faFolderTree;
        default:
            return faLock;
    }
}

export const ChangeListSection: React.FC<InjectedProps> = (props) => {
    const {title, headline, text, changeList, total} = props;
    const dispatch = useDispatch();
    const user = useUserSelector();
    const [page, setPage] = useState(1);
    const [open, setOpen] = useState(false);
    const itemsPerPage = 10;

    const filteredChangeList = props.changeList;

    const icon = useMemo(() => {
            if (open) {
                return faAngleUp;
            }
            return faAngleDown;
    }, [user, open, props.title]);

    const totalPages = Math.ceil(filteredChangeList.length / itemsPerPage);

    const renderItems = useMemo(() => {
        if (filteredChangeList.length > itemsPerPage) {
            if (filteredChangeList.length < (itemsPerPage * (page - 1))) {
                setPage(1);
            }
            return filteredChangeList.slice(
                (page - 1) * itemsPerPage,
                (page - 1) * itemsPerPage + itemsPerPage
            );
        }
        setPage(1);
        return filteredChangeList;
    }, [page, filteredChangeList]);

    const changeCount = filteredChangeList.length;
    const changePercentage = props.total === 0 ? null :
        ((changeCount / props.total) * 100) % 1 !== 0
            ? ((changeCount / props.total) * 100).toFixed(2)
            : (changeCount / props.total) * 100;


    const startItem = (page - 1) * itemsPerPage + 1;
    const endItem = Math.min(page * itemsPerPage, filteredChangeList.length);

    const handlePrevClick = () => {
        if (page > 1) {
            setPage(page - 1);
        }
    };

    const handleNextClick = () => {
        if (page < totalPages) {
            setPage(page + 1);
        }
    };

    const stopEvents = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
    };

    return filteredChangeList.length ? (
        <div className="task-list-accordion-wrap">
            <div className="task-list-accordion">
                <div
                    className="task-list-accordion-label"
                    onClick={() => setOpen(!open)}>
                    <div className={"label-chevron-text"}>
                        <FontAwesomeIcon icon={icon} className="chevron-icon"/>
                        <span className={'label-text-only'}>
                            {props.headline}
                            <span className="percentage">
                                {" "}
                                {" (" +
                                    filteredChangeList.length + `${changePercentage ?
                                    // @ts-ignore
                                    " - " + Math.round(+changePercentage) + "%)" : ")"}`}
                                {" "}
                            </span>
                        </span>
                    </div>
                        <div className="custom-pagination" onClick={stopEvents}>
                            <div className="number">
                                {startItem}-{endItem} of {filteredChangeList.length}
                            </div>
                            <button onClick={handlePrevClick} disabled={page === 1}>
                                <FontAwesomeIcon
                                    style={{fontSize: "16px"}}
                                    icon={faAngleLeft}
                                />
                            </button>
                            <button onClick={handleNextClick} disabled={page === totalPages}>
                                <FontAwesomeIcon
                                    style={{fontSize: "16px"}}
                                    icon={faAngleRight}
                                />
                            </button>
                        </div>
                </div>
            </div>
            <div
                className={classNames("task-list-accordion-content", {
                    open: open,
                })}
            >
                <div>
                    {!open
                        ? null
                        : renderItems.map((task, i) => {
                            return (
                                <div
                                    className={styles.ChangeContainer}
                                    key={task.task_id}
                                    id={task.task_id}
                                >
                                    <div className={styles.IconContainer}>
                                        <FontAwesomeIcon icon={selectIcon(title)} size={'lg'}/>
                                    </div>
                                    <div className={styles.TaskTitles}>
                                        <span className={styles.TaskName}>{task.taskName} ({task.taskCode})</span>
                                        <span className={styles.WbsName}>{task.wbsPath}</span>
                                    </div>
                                    <div className={styles.ChangeText}>
                                        {generateText(title, task)}
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>
        </div>
    ) : null;
};